import {
  DEFAULT_CHAIN_ID,
  getChainName,
  getIsSupportedChainId,
} from '@ui/constants/chain';
import { ChainId } from '@ui/types/chain';
import { getLocalStorageKey } from '@ui/utils/getLocalStorageKey';
import { useEffect } from 'react';
import { useAccount } from 'wagmi';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { useIsMounted } from '../useIsMounted';

type Store = {
  dataChain: ChainId;
  setDataChain: (chainId: ChainId) => void;
};

const useDataChainStore = create<Store>()(
  persist(
    (set) => ({
      dataChain: DEFAULT_CHAIN_ID,
      setDataChain(chainId) {
        set({ dataChain: chainId });
      },
    }),
    {
      name: getLocalStorageKey('dataChain'),
      merge(persistedState, currentState) {
        if (typeof persistedState !== 'object' || !persistedState) {
          return currentState;
        }

        if ('dataChain' in persistedState) {
          const dataChain = Number(persistedState.dataChain);
          const isValid = getIsSupportedChainId(dataChain);
          if (isValid) {
            return {
              ...currentState,
              dataChain,
            };
          }
        }

        return currentState;
      },
    },
  ),
);
export const useSyncDataChainStore = () => {
  const { isConnected, chainId } = useAccount();

  useEffect(() => {
    // sync data chain store from connected wallet supported chain
    // on refresh, or on client level chain switch
    if (
      isConnected &&
      chainId &&
      getIsSupportedChainId(chainId) &&
      useDataChainStore.getState().dataChain !== chainId
    ) {
      useDataChainStore.getState().setDataChain(chainId);
    }
  }, [chainId, isConnected]);
};

export const getChainId = () => useDataChainStore.getState().dataChain;
export const useDataChainId = () => useDataChainStore((s) => s.dataChain);
/**
 * cater ssr hydration case
 */
export const useClientDataChainId = () => {
  const isMount = useIsMounted();
  const chainId = useDataChainId();

  return isMount ? chainId : DEFAULT_CHAIN_ID;
};
export const useDataChainName = () =>
  useDataChainStore((s) => getChainName(s.dataChain));

export default useDataChainStore;
